import { Injectable } from '@angular/core';
import axios from 'axios';
import { environment } from 'src/environments/environment';
import { ClaimSums } from 'src/types/claim-sum';


export type CaseStartCountByLawFirmRow = {
  law_firm_name: string;
  product_type: string;
  product_label: string;
  count: number;
  paid_payee_fee_by_currency: Record<string, number>;
};

export type ChartParams = {
  from_date: string;
  to_date: string;
  utm_content?: string;
};

export type CaseStartsChartPoint = {
  x: string | number;
  y: string | number;
};

export type CaseStartsChartData = CaseStartsChartPoint[];

export type ChartResponse = {
  data: {
    labels: string[];
    datasets: {
      type: 'bar';
      label: string;
      data: CaseStartsChartData;
    }[];
  };
};

export type ChartName = 'case-starts';

export type CaseStartsByPartnerResponse = {
  data: {
    headers: {
      id: string;
      label: string;
    }[];
    datasets: Record<string, Record<string, number>>;
  };
};

export type SuccessfulSoftCase = {
  client_name: string | null;
  client_country: string | null;
  client_postcode: string | null;
  client_settlement: string | null;
  client_street: string | null;
  client_tax_number: string | null;
  user_email: string;
  debtor_name: string | null;
  client_id: string;
  case_id: string;
  claims: ClaimSums;
  currency_iso: string;
  closed_at: string | null;
  state: string;
  product: string;
  product_type: string;
  partner: string | null;
};

export type SuccessfulSoftCasesResponse = {
  data: SuccessfulSoftCase[];
};

export type GetCaseFeesParams = {
  started_at_from: string;
  started_at_to: string;
};

export type CaseFee = {
  month: string;
  count: number;
  partner_name: string;
  fee_name: string;
  start_fee_amount: number;
  reject_fee_amount: number;
  fee_currency: string;
  product_name: string;
};

@Injectable({
  providedIn: 'root'
})
export class ChartsService {
  async getChart(chartName: ChartName, params: ChartParams): Promise<ChartResponse> {
    const url = `${environment.baseUrl}/api/charts/${chartName}`;
    const result = await axios.get(url, {
      params,
    });
    return result.data;
  }

  async getCaseFees(params: GetCaseFeesParams): Promise<CaseFee[]> {
    const url = `${environment.baseUrl}/api/charts/get-case-fees`;
    const result = await axios.get<{ data: CaseFee[]; }>(url, {
      params,
    });
    return result.data.data;
  }

  async getCaseStartsByPartner(params: ChartParams): Promise<CaseStartsByPartnerResponse> {
    const url = `${environment.baseUrl}/api/charts/get-case-starts-by-partner`;
    const result = await axios.get(url, {
      params,
    });
    return result.data;
  }

  async getSuccessfulSoftCases(params: ChartParams): Promise<SuccessfulSoftCasesResponse> {
    const url = `${environment.baseUrl}/api/charts/get-successful-soft-cases`;
    const result = await axios.get(url, {
      params,
    });
    return result.data;
  }

  async getCaseStartCountsByTransition(params: ChartParams): Promise<CaseStartCountByLawFirmRow[]> {
    const url = `${environment.baseUrl}/api/charts/get-case-start-counts-by-transition`;
    const result = await axios.get<{ data: CaseStartCountByLawFirmRow[]; }>(url, {
      params,
    });
    return result.data.data;
  }
}
